import { useEffect, useRef, useState } from 'react'
import { apiPost } from '../api'

interface Command {
  id: string
  title: string
  group: string
  hint?: string
  run: () => Promise<void> | void
}

export default function CommandPalette({ onChangeTab }: { onChangeTab: (tab: 'work' | 'home') => void }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  // Build the command set
  const commands: Command[] = [
    {
      id: 'tab-work', group: 'Navigate', title: 'Go to Work tab',
      run: () => onChangeTab('work'),
    },
    {
      id: 'tab-home', group: 'Navigate', title: 'Go to Home · Claire tab',
      run: () => onChangeTab('home'),
    },
    {
      id: 'refresh-cost', group: 'Infra', title: 'Refresh Civo cost snapshot',
      run: async () => { await apiPost('/infra/cost/refresh'); setToast('cost refreshed') },
    },
    {
      id: 'sync-providers', group: 'Infra', title: 'Sync Railway / Vercel / Hostinger',
      run: async () => {
        const r = await apiPost<{ railway: number; vercel: number; hostinger: number; deactivated: number }>('/infra/providers/sync')
        setToast(`synced: ${r.railway} railway, ${r.vercel} vercel, ${r.hostinger} hostinger`)
      },
    },
    {
      id: 'panic', group: 'Infra · 🚨', title: 'PANIC — stop every expensive resource',
      hint: 'requires confirm',
      run: async () => {
        if (!confirm('STOP everything expensive (every GPU, kube cluster, VPS)?\nType-confirm via X-Confirm header.')) return
        const r = await fetch((import.meta.env.VITE_API_BASE as string ?? '') + '/infra/panic', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('jamie-dashboard-bearer') ?? ''}`,
            'X-Confirm': 'STOP-EVERYTHING',
            'Content-Type': 'application/json',
          },
        })
        const j = await r.json().catch(() => ({}))
        setToast(`panic: ${(j as { stopped?: string[] })?.stopped?.length ?? 0} stopped`)
      },
    },
    {
      id: 'auto-stop', group: 'Infra', title: 'Run nightly auto-stop sweep now',
      run: async () => {
        const r = await apiPost<{ stopped: string[]; errors: unknown[] }>('/infra/auto-stop/run')
        setToast(`auto-stop: ${r.stopped.length} stopped`)
      },
    },
    {
      id: 'regen-focus', group: 'AI', title: 'Regenerate Tonight\'s Focus',
      run: async () => { await apiPost('/focus/regenerate'); setToast('focus regenerated') },
    },
    {
      id: 'demo-mode', group: 'View', title: 'Toggle Demo mode (hide personal stuff)',
      run: () => {
        const cur = localStorage.getItem('jamie-dashboard:demo-mode') === '1'
        localStorage.setItem('jamie-dashboard:demo-mode', cur ? '0' : '1')
        document.body.dataset.demoMode = cur ? 'false' : 'true'
        setToast(cur ? 'demo mode OFF' : 'demo mode ON')
      },
    },
    {
      id: 'self-health', group: 'Diag', title: 'Run self-health check',
      run: async () => {
        const r = await fetch((import.meta.env.VITE_API_BASE as string ?? '') + '/selfhealth', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('jamie-dashboard-bearer') ?? ''}` },
        })
        const j = await r.json().catch(() => ({})) as { ok?: boolean; checks?: Record<string, { ok: boolean; ms?: number }> }
        const failing = Object.entries(j.checks ?? {}).filter(([_, v]) => !v.ok).map(([k]) => k)
        setToast(j.ok ? 'all bindings healthy' : `failing: ${failing.join(', ')}`)
      },
    },
    {
      id: 'create-share', group: 'Share', title: 'Create read-only status share link',
      run: async () => {
        const label = prompt('Label for this share link (visible to recipients)?') || 'untitled'
        const days = prompt('Expires in how many days? (blank = never)') || ''
        const expiresIn = days ? parseInt(days, 10) : null
        const r = await fetch((import.meta.env.VITE_API_BASE as string ?? '') + '/share/tokens', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('jamie-dashboard-bearer') ?? ''}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ label, expires_in_days: expiresIn }),
        })
        const j = await r.json().catch(() => ({})) as { url?: string; token?: string }
        if (j.url) {
          await navigator.clipboard?.writeText(j.url).catch(() => undefined)
          setToast(`URL copied: ${j.url}`)
        } else setToast('failed to create share link')
      },
    },
    {
      id: 'vip-senders', group: 'AI', title: 'Edit VIP senders (always high-priority)',
      run: async () => {
        const base = (import.meta.env.VITE_API_BASE as string ?? '')
        const bearer = localStorage.getItem('jamie-dashboard-bearer') ?? ''
        const r = await fetch(`${base}/settings/vip-senders`, { headers: { Authorization: `Bearer ${bearer}` } })
        const j = await r.json().catch(() => ({})) as { senders?: string[] }
        const current = (j.senders ?? []).join(', ')
        const next = prompt('VIP senders (comma-separated names or addresses)', current)
        if (next === null) return
        const list = next.split(',').map(s => s.trim()).filter(Boolean)
        await fetch(`${base}/settings/vip-senders`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${bearer}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ senders: list }),
        })
        setToast(`VIPs saved: ${list.length} senders`)
      },
    },
    {
      id: 'rescan-tasks', group: 'AI', title: 'Re-scan inbox for tasks (force)',
      run: async () => {
        const base = (import.meta.env.VITE_API_BASE as string ?? '')
        const bearer = localStorage.getItem('jamie-dashboard-bearer') ?? ''
        // Clear tasks_extracted_at on all rows so the next run reprocesses everything with the new rules
        const r = await fetch(`${base}/mercedes/extract-tasks`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${bearer}`, 'Content-Type': 'application/json' },
        })
        const j = await r.json().catch(() => ({})) as { extracted?: number; vip_overrides?: number }
        setToast(`re-scan: ${j.extracted ?? 0} tasks · ${j.vip_overrides ?? 0} VIP overrides`)
      },
    },
    {
      id: 'ai-usage', group: 'AI', title: 'View AI usage (last 24h)',
      run: async () => {
        const r = await fetch((import.meta.env.VITE_API_BASE as string ?? '') + '/ai/usage?hours=24', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('jamie-dashboard-bearer') ?? ''}` },
        })
        const j = await r.json().catch(() => ({})) as { total?: number; failed?: number; rows?: Array<{ feature: string; n: number }> }
        const top = (j.rows ?? []).slice(0, 3).map(r => `${r.feature}:${r.n}`).join(' · ')
        setToast(`${j.total ?? 0} AI calls / ${j.failed ?? 0} failed · ${top}`)
      },
    },
  ]

  // Cmd+K binding
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen(v => !v)
      } else if (e.key === 'Escape' && open) {
        setOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  useEffect(() => {
    if (open) {
      setQ('')
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3500)
      return () => clearTimeout(t)
    }
  }, [toast])

  const fuzzyHit = (cmd: Command) => {
    if (!q) return true
    const needle = q.toLowerCase()
    return cmd.title.toLowerCase().includes(needle) || cmd.group.toLowerCase().includes(needle)
  }
  const filtered = commands.filter(fuzzyHit)
  const grouped = filtered.reduce<Record<string, Command[]>>((acc, c) => {
    if (!acc[c.group]) acc[c.group] = []
    acc[c.group]!.push(c); return acc
  }, {})

  async function execute(c: Command) {
    setBusy(c.id)
    try { await c.run() } catch (e) { setToast(`error: ${e}`) }
    finally { setBusy(null); setOpen(false) }
  }

  return (
    <>
      {open && (
        <div className="cmdk-backdrop" onClick={() => setOpen(false)}>
          <div className="cmdk-panel" onClick={e => e.stopPropagation()}>
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && filtered[0]) execute(filtered[0])
              }}
              placeholder='type a command…'
              autoFocus
            />
            <div className="cmdk-results">
              {Object.entries(grouped).map(([group, items]) => (
                <div key={group}>
                  <div className="cmdk-group">{group}</div>
                  {items.map(c => (
                    <button key={c.id} type="button" onClick={() => execute(c)} disabled={busy === c.id}
                      className="cmdk-item">
                      <span>{c.title}</span>
                      {c.hint && <span className="muted mono" style={{ fontSize: '0.6rem' }}>{c.hint}</span>}
                    </button>
                  ))}
                </div>
              ))}
              {filtered.length === 0 && (
                <div style={{ padding: '12px', color: 'var(--text-muted)', fontSize: '0.85rem' }}>no commands match</div>
              )}
            </div>
            <div className="cmdk-footer">
              <span>↵ run</span>
              <span>esc close</span>
              <span>⌘K toggle</span>
            </div>
          </div>
        </div>
      )}
      {toast && <div className="cmdk-toast">{toast}</div>}
    </>
  )
}
